import { AxiosRequestConfig } from 'axios';
import { PresignedUrlResponse, FileUploadOptions, createApiRequest } from "../common";
import { rawAxios } from "../common/apiClient";

/**
 * Uploads a file to S3 using a presigned URL
 * @param file File to upload
 * @param presignedData Presigned URL response from the API
 * @param options Optional configuration for the upload
 * @returns Promise resolving to the file key string or throws an error
 */
export const uploadFileWithPresignedUrl = async (
  file: File,
  presignedData: PresignedUrlResponse,
  options?: FileUploadOptions
): Promise<string> => {
  const { url, fields } = presignedData;
  
  const formData = new FormData();
  
  // Add all fields from presigned URL
  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  
  // Add any additional fields
  if (options?.additionalFields) {
    Object.entries(options.additionalFields).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }
  
  // Add the file last
  formData.append('File', file);
  
  const config: AxiosRequestConfig = {
    onUploadProgress: (progressEvent) => {
      if (options?.onProgress && progressEvent.total) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        options.onProgress(percentCompleted);
      }
    },
  };
  
  // Upload to S3 directly (not through our API)
  await rawAxios.post(url, formData, config);
  
  // Return the key from fields.key, which is what our API uses
  return presignedData.key || presignedData.fields.key || '';
};

/**
 * Generate a presigned URL specifically for contract PDFs
 * @param contractId ID of the contract to read
 * @returns Promise with contract read URL
 */
export const getPresignedContractReadUrlApi = async (
  contractId: string
): Promise<{ url: string }> => {
  const request = createApiRequest<{ url: string }>('post', '/getContractReadURL');
  
  return request({ contractId });
};